import { inject, Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { IDENTIFIER_REQUIRED } from '../../../constants';
import {
  AuthAccessTokenDetails,
  ILoginUserNamePasswordInput,
  LoginAuthWithCredentialsResponse,
  LoginOAuthParams,
  SwapAuthForAccessTokens
} from '../../../models';
import { SwapAccessTokenService } from '../../swap-access-token-service/swap-access-token-service';
import { LoginAlternativeAuthenticationService } from '../login-passwordless-adapter-service/login-alternative-authentication/login-alternative-authentication.service';
import { LoginAuthorizationService } from '../login-remember-me-adapter-service/login-authorization/login-authorization-service';
import { LoginPostWithFingerPrintService } from '../login-remember-me-adapter-service/login-post-with-fingerprint/login-post-with-fingerprint-service';
import {
  isUserNamePasswordRequired,
  transformSwapAuthTokenDetails
} from '../login-remember-me-utils-service/initiate-login-utils';
import { LoginRememberMeErrorUtilService } from '../login-remember-me-utils-service/login-remember-me-error-util.service';
import { LoginRememberMeUtilsService } from '../login-remember-me-utils-service/login-remember-me-utils.service';
import { LoginSubmitAuthenticationIdentifierService } from './login-submit-authentication-identifier.service';

@Injectable({
  providedIn: 'root'
})
export class LoginPasswordlessFacadeHelperService {
  retryCount = 0;

  loginAuthorization = inject(LoginAuthorizationService);
  loginPostWithFingerPrint = inject(LoginPostWithFingerPrintService);
  loginRememberMeUtils = inject(LoginRememberMeUtilsService);
  loginRememberMeErrorUtil = inject(LoginRememberMeErrorUtilService);
  swapAccessToken = inject(SwapAccessTokenService);
  loginSubmitAuthenticationIdentifier = inject(
    LoginSubmitAuthenticationIdentifierService
  );

  loginAlternativeAuthentication = inject(
    LoginAlternativeAuthenticationService
  );

  savedTokenCheck(response: SwapAuthForAccessTokens): boolean {
    return !!(response.encyToken && response.uid);
  }

  processResponse(
    response: LoginAuthWithCredentialsResponse | SwapAuthForAccessTokens,
    loginOAuthParams: LoginOAuthParams
  ): Observable<LoginAuthWithCredentialsResponse | AuthAccessTokenDetails> {
    if (isUserNamePasswordRequired(response, loginOAuthParams)) {
      return of(response);
    } else if ('access_token' in response) {
      this.retryCount = 0;
      const authAccessTokenDetails: AuthAccessTokenDetails =
        transformSwapAuthTokenDetails(response);
      if (this.savedTokenCheck(response)) {
        return of({
          ...authAccessTokenDetails,
          encyToken: response.encyToken,
          uid: response.uid
        });
      } else {
        return of(authAccessTokenDetails);
      }
    } else {
      return throwError(response);
    }
  }

  checkFlowId(response: LoginAuthWithCredentialsResponse): string {
    return response?.id ? response.id : '';
  }

  handleIdentifierRequired(
    response: LoginAuthWithCredentialsResponse,
    loginOAuthParams: LoginOAuthParams
  ): Observable<AuthAccessTokenDetails | string> {
    if (response.previouslySubmittedIdentifiers?.length) {
      const requestInput: ILoginUserNamePasswordInput = {
        userName: response.previouslySubmittedIdentifiers[0],
        rememberMe: 'Y',
        password: ''
      };
      return this.loginSubmitAuthenticationIdentifier.submitAuthenticationIdentifier(
        loginOAuthParams,
        requestInput,
        this.checkFlowId(response)
      );
    } else {
      this.loginSubmitAuthenticationIdentifier.flowId =
        this.checkFlowId(response);
      return of(response.status || IDENTIFIER_REQUIRED);
    }
  }
}
